import { useEffect, useState } from 'react';

import { IconRefresh } from '@tabler/icons-react';
import { useAtom } from 'jotai';

import useFeatureFlag from 'hooks/useFeatureFlag';
import { useSelectedDealer } from '@lotlinx-aws/dealer-selector';
import useGetCacheStatus, {
  GetStatusResponse,
  useGetInitialCacheStatus,
} from 'pages/Settings/CacheRebuild/api/useGetCacheStatus/useGetCacheStatus';
import useInitiateCacheRebuild from 'pages/Settings/CacheRebuild/api/useInitiateCacheRebuild/useInitiateCacheRebuild';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import Spinner from '@lotlinx-aws/sparky-component-library/Sparky/Spinner';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';
import { userAtom } from 'store';
import { FeatureFlag } from 'types/FeatureFlags';

import { cachedDealerListAtom } from './cachedDealerListAtom';
import styles from './CacheRefresh.module.scss';

export interface CacheRefreshProps {
  padding?: string;
  margin?: string;
  width?: string;
}

/**
 * CacheRefresh
 */
export default function CacheRefresh({ padding, margin, width }: CacheRefreshProps) {
  const [lastCacheUpdate, setLastCacheUpdate] = useState('');
  const [isCacheRefreshing, setIsCacheRefreshing] = useState<boolean>(false);

  const [cacheDealerList, setCacheDealerList] = useAtom(cachedDealerListAtom);
  const [user] = useAtom(userAtom);

  const enableCacheRebuild = useFeatureFlag(FeatureFlag.ENABLE_CACHE_REBUILD);
  const { dealer } = useSelectedDealer();
  const { addToast } = useToast();

  const isCacheRebuildEnabled =
    user?.accountType === 'OWNER' && enableCacheRebuild && user.email.indexOf('@lotlinx.com') > -1;

  const response = useGetCacheStatus(cacheDealerList);
  const initialResponse = useGetInitialCacheStatus();

  useEffect(() => {
    if (!initialResponse.isLoading && initialResponse?.data?.lastUpdated) {
      setLastCacheUpdate(initialResponse?.data?.lastUpdated);
    }
  }, [initialResponse, initialResponse.isLoading]);

  // Reset flag on dealer change
  useEffect(() => {
    if (isCacheRebuildEnabled) {
      setIsCacheRefreshing(false);
    }
  }, [dealer?.id]);

  const checkCacheStatus = () => {
    if (dealer?.id && response.length > 0) {
      let lastCacheEntry = //@ts-ignore
        response.find((r) => r?.data?.dealerId === dealer?.id)?.data?.lastUpdated;
      if (lastCacheEntry) {
        setLastCacheUpdate(lastCacheEntry);
      }
      if (cacheDealerList.length > 0) {
        let cacheList = [...cacheDealerList];
        response.forEach((entry) => {
          if (!entry.isLoading && !entry.isRefetching) {
            let data: GetStatusResponse = entry.data as GetStatusResponse;
            if (data?.status === 'RUNNING' || data?.status === 'PENDING') {
              // If the cacheRefreshing flag isn't active, but the current active dealer refresh is running, set flag to true
              if (data.dealerId === dealer.id && !isCacheRefreshing) {
                setIsCacheRefreshing(true);
              }
            }

            if (data?.status === 'COMPLETE') {
              // If the flag is true, and data is current dealer, set flag false
              if (data.dealerId === dealer.id && isCacheRefreshing) {
                setIsCacheRefreshing(false);
              }
              // Give toast notifying the refresh is complete, and remove from atom
              addToast({
                status: 'success',
                description: `Cache reset for dealer id ${data?.dealerId}`,
                duration: 30000,
              });
              cacheList = cacheDealerList.filter((e) => e !== data.dealerId);
            }

            if (data?.status === 'ERROR' || data?.status === 'UNKNOWN') {
              // If the flag is true, and data is current dealer, set flag false
              if (data.dealerId === dealer.id && isCacheRefreshing) {
                setIsCacheRefreshing(false);
              }

              addToast({
                status: 'error',
                description: `There was an issue with the cache reset for dealer id ${data?.dealerId}`,
                duration: 30000,
              });
              cacheList = cacheDealerList.filter((e) => e !== data.dealerId);
            }
          }
        });
        if (cacheList.toString() !== cacheDealerList.toString()) {
          setCacheDealerList(cacheList);
        }
      }
    }
  };

  useEffect(() => {
    if (isCacheRebuildEnabled) {
      checkCacheStatus();
    }
  }, [dealer, response]);

  const cacheMutation = useInitiateCacheRebuild();

  const handleCacheRefreshClick = () => {
    if (dealer?.id) {
      cacheMutation.mutate(undefined, {
        onSuccess: () => {
          if (cacheDealerList.findIndex((r) => r === dealer?.id) === -1) {
            setCacheDealerList([dealer?.id, ...cacheDealerList]);
          }
          setIsCacheRefreshing(true);
        },
        onError: (err) => {
          if (
            `${err}` === 'Error: A cache build is already in process for this dealer (Error -1)'
          ) {
            setIsCacheRefreshing(true);
            if (cacheDealerList.findIndex((r) => r === dealer?.id) === -1) {
              setCacheDealerList([dealer?.id, ...cacheDealerList]);
            }
            addToast({
              status: 'error',
              description: 'A cache build has already been started',
            });
          }
        },
      });
    } else {
      addToast({
        status: 'error',
        description: 'There was an error starting the refresh, Please check a dealer is selected',
      });
    }
  };

  return isCacheRebuildEnabled ? (
    <>
      {!isCacheRefreshing ? (
        <Flex
          padding={padding ? padding : '24px 0 12px'}
          margin={margin ? margin : '0'}
          rowGap='12px'
          width={width ? width : '100%'}
          alignItems='center'
          column
        >
          <Button
            className={styles.cacheButton}
            variant='outline'
            autoFocus={false}
            aria-label='refresh cache'
            onClick={handleCacheRefreshClick}
          >
            Refresh cache <IconRefresh className={styles.cacheIcon} />
          </Button>
          <Text type='body-compact-01' color='secondary'>
            Last cache - {lastCacheUpdate}
          </Text>
        </Flex>
      ) : (
        <Flex
          padding={padding ? padding : '24px 0 12px'}
          margin={margin ? margin : '0'}
          rowGap='12px'
          width={width ? width : '100%'}
          alignItems='center'
          column
        >
          <Button
            className={styles.cacheButton}
            variant='outline'
            autoFocus={false}
            aria-label='refresh cache'
            onClick={handleCacheRefreshClick}
            disabled
          >
            Cache Refreshing
            <Spinner className={styles.settingsSpinner} size='lg' />
          </Button>
        </Flex>
      )}
    </>
  ) : (
    <></>
  );
}
